import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Empty, Spin, Typography, Alert } from 'antd';
import { ShoppingOutlined, EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { fetchSalesOrders, type SalesOrder } from '@digiwedge/axiz_api';

const { Title, Text } = Typography;

const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    New: 'blue',
    Processing: 'orange',
    Shipped: 'cyan',
    Delivered: 'green',
    Cancelled: 'red',
    Refunded: 'purple',
  };
  return statusColors[status] || 'default';
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
};

export function OrderHistoryPage() {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const profile = useGlobalStore((s: GlobalState) => s.profile);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/orders' } });
      return;
    }

    const loadOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!profile?.email && !profile?.userId) {
          setError('Profile information is missing. Please sign out and back in.');
          return;
        }
        // Fetch orders filtered by userId (preferred) or email (fallback)
        // Backend should prioritize userId for authenticated user queries
        const userOrders = await fetchSalesOrders({
          userId: profile.userId || undefined,
          customerEmail: profile.email,
          pageSize: 50,
          page: 1,
        });
        setOrders(userOrders);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [isAuthenticated, navigate, profile?.email, profile?.userId]);

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'reference',
      key: 'reference',
      render: (ref: string) => <Text strong>{ref}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => <Text strong>{formatAmount(amount)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: SalesOrder) => (
        <Link to={`/orders/${record.id}`}>
          <Button type="link" icon={<EyeOutlined />}>
            View
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/">
          <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }}>
            Back to Shop
          </Button>
        </Link>
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <ShoppingOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <Title level={3} style={{ margin: 0 }}>
            My Orders
          </Title>
        </div>

        {error && (
          <Alert
            type="error"
            message="Error Loading Orders"
            description={error}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16 }}>Loading your orders...</Text>
          </div>
        ) : orders.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                You haven't placed any orders yet.
                <br />
                <Link to="/">Start shopping</Link> to see your orders here.
              </span>
            }
          />
        ) : (
          <Table
            dataSource={orders}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `${total} orders`,
            }}
          />
        )}
      </Card>
    </div>
  );
}

export default OrderHistoryPage;
